// ══════════════════════════════════════════════════════════════════════════════════════════════
//  LA NUBE · LAS SUBIDAS, EN TODA LA APLICACIÓN (1-oct-2026, B-1013)
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  El dueño: «al subir un archivo manualmente a la nube desde /nube, debe permitirme moverme por la app sin ningún
//  problema y poder trabajar libremente una vez yo ya he seleccionado que documento quiero subir».
//
//  Va montado en App.jsx, AL LADO de las rutas y por encima de los tres diseños de pantalla (el de la clínica, el de
//  administración y el de plataforma): ir de la Nube a la agenda, a una ficha o a Ajustes no lo desmonta. La cola es la de
//  utils/nubeSubidasGlobal.js (una por sesión y clínica); esto es lo que se ve y lo que la cuida:
//   · la pastilla de abajo a la derecha, siempre visible mientras haya algo (components/nube/ColaSubidas.jsx): progreso,
//     cuántos quedan, Pausar o Seguir, Cancelar, los errores con su texto y «Reintentar»;
//   · al acabar, un aviso;
//   · al cerrar o recargar la pestaña con subidas sin terminar, el aviso del navegador; y al volver, lo que quedó a
//     medias (el servidor lo guarda 24 horas) se ofrece para seguir eligiendo otra vez el mismo archivo;
//   · al cerrar sesión, entrar con otra cuenta o cambiar de clínica, se cancelan y se dice cuántas (nunca siguen subiendo
//     con otra identidad: lo corta utils/nubeSubidasGlobal antes de que salga una sola petición);
//   · la pantalla no se apaga mientras se sube.
import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import api, { clinicaDeSesion, suscribirVisita, enVisitaDelSoporte, soporteConAccesoTotal } from '../../services/api'
import { alLimpiarSesion } from '../../services/sesion'
import { useAuth } from '../../context/AuthContext'
import { useToast } from '../../context/ToastContext'
import { confirmar } from '../../context/DialogosContext'
import usePermisos from '../../hooks/usePermisos'
import usePantallaDespierta from '../../hooks/usePantallaDespierta'
import ColaSubidas from './ColaSubidas'
import {
  configurarSubidas, colaDeLaSesion, colaSiHay, suscribirSubidas, revisarSesion, alCancelarPorCambio, haySubidasSinTerminar,
  subidasPlegadas, plegarSubidas, ponerPregunta,
} from '../../utils/nubeSubidasGlobal'
import { resumenAlAcabar, esElMismoArchivo, TXT_ANTES_DE_CERRAR, textoCanceladasPorCambio } from '../../utils/nubeSubidasAvisos'
import { t } from '../../utils/i18n'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

// QUIÉN Y EN QUÉ CLÍNICA: lo que hace que una cola sea de una sesión. Se lee de donde lo lee services/api (la visita
// manda sobre la clínica de la cuenta), para que no haya dos criterios de «dónde estoy». Sin sesión, ''.
export function identidadDeLaSesion() {
  try {
    const u = JSON.parse(localStorage.getItem('user') || 'null')
    if (!u || !u.id || !localStorage.getItem('token')) return ''
    return 'u' + u.id + ':c' + (clinicaDeSesion() || '')
  } catch (_) { return '' }
}

// AL CARGAR EL MÓDULO (antes de pintar nada): con qué se sube, cómo se lee la identidad, y los dos avisos de que ha
// cambiado. No dependen de que haya ningún componente montado: cerrar sesión desde la pantalla de bloqueo, un 401 o una
// visita cortada desde otra pestaña también abandonan la cola en el momento.
configurarSubidas({ api, identidad: identidadDeLaSesion })
ponerPregunta((o) => confirmar(o))
alLimpiarSesion(() => { revisarSesion() })
suscribirVisita(() => { revisarSesion() })

// La pastilla va a la izquierda del botón redondo de ayuda (16 + 52 + 10): no se tapan.
const DERECHA = 78

export default function SubidasGlobales() {
  const { realUser, token } = useAuth() || {}
  if (!realUser || !token) return null
  return <SubidasDeLaSesion />
}

// (Exportada para su guardia, que la monta directamente: backend/routes/nube-subidas-segundo-plano.test.js.)
export function SubidasDeLaSesion() {
  // La identidad se lee AQUÍ, en cada pintado: entrar o salir de una clínica no cambia la cuenta (quien monta esto no se
  // vuelve a pintar), pero la cola avisa y esto sí.
  const identidad = identidadDeLaSesion()
  const toast = useToast()
  const { perms, permisosListos } = usePermisos()
  const location = useLocation()
  const navigate = useNavigate()
  const [, setVersion] = useState(0)
  const inputOtraVez = useRef(null)
  const pendienteElegido = useRef(null)
  const habiaAlgo = useRef(false)
  const contadoHasta = useRef(0)

  // La Nube de ESTA persona: encendida en la clínica y con su permiso (lo mismo que decide la entrada del menú).
  const conNube = permisosListos && Array.isArray(perms && perms._sistemas_apagados) && !perms._sistemas_apagados.includes('nube') && perms.nube !== false
  const visita = enVisitaDelSoporte() && !(typeof soporteConAccesoTotal === 'function' && soporteConAccesoTotal())   // el nivel 3 sube, como la administración

  useEffect(() => suscribirSubidas(() => setVersion((n) => n + 1)), [])
  // Si la identidad cambia sin que nadie avise (otra pestaña), se nota al pintar. Y las cuentas son de cada sesión.
  useEffect(() => { revisarSesion(); habiaAlgo.current = false; contadoHasta.current = 0 }, [identidad])
  useEffect(() => alCancelarPorCambio(({ canceladas }) => {
    if (canceladas > 0) toast.warning(textoCanceladasPorCambio(canceladas), 12000)
  }), [toast])

  // Lo que quedó a medias de otra vez (solo las mías; el servidor las guarda 24 horas). Una vez por sesión y clínica.
  useEffect(() => {
    if (!conNube || visita || !identidad) return undefined
    let vivo = true
    api.get('/nube/subidas').then((r) => {
      const lista = (r.data && r.data.subidas) || []
      // Sin nada a medias no se crea ninguna cola (ni se pinta nada); la respuesta de otra sesión no se usa.
      if (vivo && lista.length && identidad === identidadDeLaSesion()) colaDeLaSesion().ponerPendientes(lista)
    }).catch(() => {})
    return () => { vivo = false }
  }, [conNube, visita, identidad])

  const cola = colaSiHay()
  const estado = cola ? cola.estado() : null
  const pendientes = cola ? cola.pendientes() : []
  const sinTerminar = !!(cola && cola.quedaAlgo())
  const subiendo = sinTerminar && !(estado && estado.pausada)

  // Mientras se sube, la pantalla no se apaga; y cerrar o recargar la pestaña pregunta antes (el navegador pone su texto).
  usePantallaDespierta(subiendo)
  useEffect(() => {
    if (!sinTerminar || typeof window === 'undefined') return undefined
    const antes = (e) => {
      if (!haySubidasSinTerminar()) return undefined   // ya se han cancelado (al cerrar sesión): se sale sin preguntar
      e.preventDefault(); e.returnValue = TXT_ANTES_DE_CERRAR; return TXT_ANTES_DE_CERRAR
    }
    window.addEventListener('beforeunload', antes)
    return () => window.removeEventListener('beforeunload', antes)
  }, [sinTerminar])

  // AL ACABAR, AVISO: cuando deja de haber algo sin terminar.
  useEffect(() => {
    if (sinTerminar) { habiaAlgo.current = true; return }
    if (!habiaAlgo.current) return
    habiaAlgo.current = false
    const r = resumenAlAcabar(estado, contadoHasta.current)   // solo la tanda de ahora, no lo de antes
    if (!r) return
    contadoHasta.current = r.hasta
    if (r.tipo === 'bien') toast.success(r.texto, 7000)
    else toast.warning(r.texto, 12000)
    if (r.tipo !== 'bien') plegarSubidas(false)
  }, [sinTerminar])   // eslint-disable-line react-hooks/exhaustive-deps

  // Fuera de la Nube, plegada: una pastilla discreta. (En la Nube la despliega la página al añadir archivos.)
  const enLaNube = location.pathname === '/nube'
  useEffect(() => { if (!enLaNube) plegarSubidas(true) }, [enLaNube, location.pathname])

  if (!cola || visita) return null

  const elegirOtraVez = (p) => { pendienteElegido.current = p; if (inputOtraVez.current) inputOtraVez.current.click() }
  const alElegir = (e) => {
    const f = e.target && e.target.files && e.target.files[0]
    const p = pendienteElegido.current
    pendienteElegido.current = null
    if (e.target) e.target.value = ''
    if (!f || !p) return
    if (!esElMismoArchivo(p, f)) { toast.warning(t('components.nube.SubidasGlobales.ese_no_es_el_archivo', 'Ese no es el archivo que quedó a medias (tiene otro nombre u otro tamaño). Para seguir por donde iba, elige «{{nombre}}».', { nombre: p.nombre }), 9000); return }
    cola.anadir([{ file: f, carpetaId: p.carpeta_id, carpetaNombre: null }])
    plegarSubidas(false)
  }
  const cancelarTodo = async () => {
    const n = estado ? estado.sinTerminar : 0
    const ok = await confirmar({ titulo: t('components.nube.SubidasGlobales.cancelar_las_subidas', 'Cancelar las subidas'), mensaje: t('components.nube.SubidasGlobales.vas_a_cancelar_lo_que', 'Vas a cancelar {{x}}. Lo que ya se ha subido del todo se queda en la Nube.', { x: n === 1 ? t('components.nube.SubidasGlobales.la_subida_que_queda', 'la subida que queda') : t('components.nube.SubidasGlobales.las_subidas_que_quedan', 'las {{n}} subidas que quedan', { n }) }), textoConfirmar: t('components.nube.SubidasGlobales.cancelar_las_subidas', 'Cancelar las subidas'), textoCancelar: t('components.nube.SubidasGlobales.seguir_subiendo', 'Seguir subiendo') })
    if (ok) cola.cancelarTodo()
  }

  return (
    <>
      <ColaSubidas estado={estado} pendientes={pendientes} plegada={subidasPlegadas()} derecha={DERECHA}
        onPlegar={() => plegarSubidas(!subidasPlegadas())}
        onPausar={() => cola.pausar()} onReanudar={() => cola.reanudar()}
        onCancelar={(id) => cola.cancelar(id)} onCancelarTodo={cancelarTodo}
        onReintentar={(id) => cola.reintentar(id)} onLimpiar={() => cola.limpiarTerminadas()}
        onDescartarPendiente={(p) => cola.descartarPendiente(p.id)} onElegirOtraVez={elegirOtraVez}
        onIrALaNube={enLaNube ? null : () => navigate('/nube')} />
      <input ref={inputOtraVez} type="file" hidden onChange={alElegir} />
    </>
  )
}
